const ADMISSION_PREFIX = 'npc:guest:spectator:admission:';
const SESSION_ID_KEY = 'npc:guest:spectator:session_id';
const DEFAULT_LOCAL_TTL_MS = 30 * 60 * 1000;
export const GUEST_SPECTATOR_RESET_EVENT = 'npc:guest-spectator-reset';

function normalizeCode(value) {
  return String(value || '').trim();
}

function randomId() {
  try {
    if (globalThis?.crypto?.randomUUID) return globalThis.crypto.randomUUID().replace(/-/g, '');
  } catch (_) {}
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
}

export function getOrCreateGuestSpectatorSessionId() {
  try {
    const existing = normalizeCode(sessionStorage.getItem(SESSION_ID_KEY));
    if (existing) return existing;
    const next = `guest_${randomId()}`.slice(0, 120);
    sessionStorage.setItem(SESSION_ID_KEY, next);
    return next;
  } catch (_) {
    return `guest_${randomId()}`.slice(0, 120);
  }
}

function admissionKey(tableCode) {
  const tc = normalizeCode(tableCode);
  return tc ? `${ADMISSION_PREFIX}${tc}` : '';
}

export function saveGuestSpectatorAdmission(payload = {}) {
  const tableCode = normalizeCode(payload?.table_code || payload?.tableCode);
  const displayCode = normalizeCode(payload?.display_code || payload?.displayCode);
  const guestToken = normalizeCode(payload?.guest_token || payload?.guestToken);
  if (!tableCode || !displayCode || !guestToken) return null;

  const expiresAtMs = Number(payload?.expires_at_ms || payload?.expiresAtMs || 0) || (Date.now() + DEFAULT_LOCAL_TTL_MS);
  const normalized = {
    ...payload,
    mode: 'guest_spectator',
    table_code: tableCode,
    tableCode,
    display_code: displayCode,
    displayCode,
    guest_token: guestToken,
    guestToken,
    game_socket_url: normalizeCode(payload?.game_socket_url || payload?.gameSocketUrl) || null,
    guest_session_id: normalizeCode(payload?.guest_session_id || payload?.guestSessionId) || getOrCreateGuestSpectatorSessionId(),
    expires_at_ms: expiresAtMs,
    saved_at_ms: Date.now(),
  };

  try { localStorage.setItem(admissionKey(tableCode), JSON.stringify(normalized)); } catch (_) {}
  return normalized;
}

export function getGuestSpectatorAdmission(tableCode) {
  const tc = normalizeCode(tableCode);
  const key = admissionKey(tc);
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) || {};
    const parsedTableCode = normalizeCode(parsed?.table_code || parsed?.tableCode);
    const token = normalizeCode(parsed?.guest_token || parsed?.guestToken);
    const expiresAtMs = Number(parsed?.expires_at_ms || parsed?.expiresAtMs || 0) || 0;
    if (!token || parsedTableCode !== tc || !expiresAtMs || expiresAtMs <= Date.now()) {
      localStorage.removeItem(key);
      return null;
    }
    return parsed;
  } catch (_) {
    try { localStorage.removeItem(key); } catch (_) {}
    return null;
  }
}

export function hasGuestSpectatorAdmission(tableCode) {
  return !!getGuestSpectatorAdmission(tableCode);
}

export function clearGuestSpectatorAdmission(tableCode) {
  const key = admissionKey(tableCode);
  if (!key) return false;
  try { localStorage.removeItem(key); return true; } catch (_) { return false; }
}

export function clearAllGuestSpectatorAdmissions() {
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = String(localStorage.key(i) || '');
      if (key.startsWith(ADMISSION_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => localStorage.removeItem(key));
    return true;
  } catch (_) {
    return false;
  }
}

function listGuestSpectatorAdmissionTableCodes() {
  const tableCodes = [];
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = String(localStorage.key(i) || '');
      if (!key.startsWith(ADMISSION_PREFIX)) continue;
      const tableCode = normalizeCode(key.slice(ADMISSION_PREFIX.length));
      if (tableCode) tableCodes.push(tableCode);
    }
  } catch (_) {}
  return Array.from(new Set(tableCodes));
}

function clearMatchingRecoveryHints(tableCodes = []) {
  const codes = new Set((tableCodes || []).map(normalizeCode).filter(Boolean));
  if (!codes.size) return;
  try {
    for (const key of ['currentTableCode', 'lastTableCode', 'tableCode']) {
      const value = normalizeCode(localStorage.getItem(key));
      if (value && codes.has(value)) localStorage.removeItem(key);
    }
  } catch (_) {}

  // Phase 5 이전 코드가 Guest 팝업을 desktop_multitable_v1에 남긴 경우만 제거한다.
  // 로그인 사용자의 다른 테이블 screen은 보존한다.
  try {
    const raw = localStorage.getItem('desktop_multitable_v1');
    const parsed = raw ? JSON.parse(raw) : null;
    if (parsed && Array.isArray(parsed.screens)) {
      const nextScreens = parsed.screens.filter((screen) => {
        const tableCode = normalizeCode(
          screen?.tableId ||
          (String(screen?.id || '').startsWith('table:') ? String(screen.id).slice('table:'.length) : '')
        );
        return !tableCode || !codes.has(tableCode);
      });
      const nextActiveId = nextScreens.some((screen) => screen?.id === parsed.activeId) ? parsed.activeId : 'lobby';
      localStorage.setItem('desktop_multitable_v1', JSON.stringify({ ...parsed, screens: nextScreens, activeId: nextActiveId }));
    }
  } catch (_) {}
}

/**
 * Guest spectator state is deliberately isolated from authenticated recovery state.
 * Call this before transitioning Guest -> Auth, Auth -> Guest/logout, or when a
 * guest admission becomes invalid. It never removes authenticated user recovery
 * rows except stale table hints that point to a known guest admission.
 */
export function clearGuestSpectatorSessionState({ reason = 'guest_session_reset' } = {}) {
  const tableCodes = listGuestSpectatorAdmissionTableCodes();
  clearMatchingRecoveryHints(tableCodes);
  clearAllGuestSpectatorAdmissions();
  try { sessionStorage.removeItem(SESSION_ID_KEY); } catch (_) {}
  try {
    window.dispatchEvent(new CustomEvent(GUEST_SPECTATOR_RESET_EVENT, {
      detail: { reason: String(reason || 'guest_session_reset'), tableCodes, ts: Date.now() },
    }));
  } catch (_) {}
  return tableCodes;
}
